import React, { useMemo, useState, useEffect } from "react";
import { InputNumber } from "primereact/inputnumber";
import { InputText } from "primereact/inputtext";
import { Dialog } from "primereact/dialog";
import { ToggleButton } from "primereact/togglebutton";
import { Button } from "primereact/button";
import { t } from "i18next";

function FiltersDialog({
  visible,
  onHide,
  type,
  idValue,
  setIdValue,
  sampleTime,
  setSampleTime,
  topNumber,
  setTopNumber,
  onlyActive,
  setOnlyActive,
  hasId,
  hasSampleTime,
  hasTop,
  hasOnlyActive,
  whereMeasures,
  setWhereMeasures,
}) {
  const idText = type === "alarms" ? "ID" : "ID Value";

  // 🔹 Pick the correct environment variable based on type
  const rawFilterString = useMemo(() => {
    switch (type) {
      case "endprocess":
        return process.env.REACT_APP_ENDPROCESS_MEASURES_FILTER || "";
      case "realtime":
        return process.env.REACT_APP_REALTIME_MEASURES_FILTER || "";
      case "alarms":
        return process.env.REACT_APP_ALARMS_MEASURES_FILTER || "";
      default:
        return "";
    }
  }, [type]);

  // 🔹 Parse filters into array
  const dynamicFilters = useMemo(() => {
    return rawFilterString
      .replace(/[\[\]]/g, "")
      .split(";")
      .map((f) => f.trim())
      .filter((f) => f.length > 0);
  }, [rawFilterString]);

  // 🔹 State for filter input values
  const [dynamicValues, setDynamicValues] = useState(
    () =>
      whereMeasures || Object.fromEntries(dynamicFilters.map((f) => [f, ""]))
  );

  // 🔹 Update state if filters change (e.g., when type changes)
  useEffect(() => {
    setDynamicValues(
      Object.fromEntries(
        dynamicFilters.map((f) => [f, whereMeasures?.[f] || ""])
      )
    );
  }, [dynamicFilters, whereMeasures]);

  // 🔹 Handle user typing and sync with parent
  const handleDynamicChange = (filterKey, value) => {
    setDynamicValues((prev) => {
      const updated = { ...prev, [filterKey]: value };
      setInternalWhereMeasures(updated);
      return updated;
    });
  };

  const [internalIdValue, setInternalIdValue] = useState(idValue || "");
  const [internalSampleTime, setInternalSampleTime] = useState(sampleTime);
  const [internalTopNumber, setInternalTopNumber] = useState(topNumber);
  const [internalOnlyActive, setInternalOnlyActive] = useState(onlyActive);
  const [internalWhereMeasures, setInternalWhereMeasures] = useState(
    whereMeasures || {}
  );

  const saveClicked = () => {
    if(setIdValue) setIdValue(internalIdValue);
    if(setSampleTime)setSampleTime(internalSampleTime);
    if(setTopNumber)setTopNumber(internalTopNumber);
    if(setOnlyActive)setOnlyActive(internalOnlyActive);
    if(setWhereMeasures) setWhereMeasures(internalWhereMeasures);
    onHide();
  };

  return (
    <Dialog
      style={{ width: "600px" }}
      header={
        <div style={{ display: "flex", alignItems: "center" }}>
          <i className="pi pi-filter" style={{ marginRight: "0.5rem" }}></i>
          {t("filters")}
        </div>
      }
      visible={visible}
      onHide={onHide}
    >
      <div
        style={{
          display: "grid",
          marginTop: "30px",
          gridTemplateColumns: "1fr 1fr",
          rowGap: "20px",
          columnGap: "1px",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {/* ✅ Static filters */}
        {hasId && (
          <>
            <label htmlFor="idValue">{idText}</label>
            <InputText
              id="idValue"
              value={internalIdValue || ""}
              placeholder="Enter ID"
              style={{ width: "250px" }}
              onChange={(e) => setInternalIdValue(e.target.value)}
            />
          </>
        )}

        {hasSampleTime && (
          <>
            <label htmlFor="sampleTime">Sample Time</label>
            <InputNumber
              id="sampleTime"
              value={internalSampleTime}
              onChange={(e) => setInternalSampleTime(e.value)}
              suffix=" s"
              placeholder="Enter sample time"
              min={10}
              style={{ width: "250px" }}
            />
          </>
        )}

        {hasTop && (
          <>
            <label htmlFor="topNumber">Max Records</label>
            <InputNumber
              id="topNumber"
              value={topNumber}
              placeholder="Enter max records"
              onChange={(e) => setInternalTopNumber(e.value)}
              style={{ width: "250px" }}
            />
          </>
        )}

        {hasOnlyActive && (
          <>
            <label htmlFor="onlyActive">Only Active</label>
            <ToggleButton
              id="onlyActive"
              checked={internalOnlyActive}
              onChange={(e) => setInternalOnlyActive(e.value)}
              offLabel="No"
              onLabel="Yes"
            />
          </>
        )}

        {/* 🔹 Dynamic filters (based on type) */}
        {dynamicFilters.map((filterKey) => (
          <React.Fragment key={filterKey}>
            <label htmlFor={filterKey}>{filterKey}</label>
            <InputText
              id={filterKey}
              value={dynamicValues[filterKey] || ""}
              placeholder={`Enter ${filterKey}`}
              style={{ width: "250px" }}
              onChange={(e) => handleDynamicChange(filterKey, e.target.value)}
            />
          </React.Fragment>
        ))}
      </div>
      {/* Div for button save */}
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          marginTop: "30px",
        }}
      >
        <Button label={t("save")} icon="pi pi-save" onClick={saveClicked} style={{width: "150px"}} />
      </div>
    </Dialog>
  );
}

export default FiltersDialog;
